/**
 * EmailCalcButton — Universal "Email me this calculation" trigger.
 *
 * Usage:
 *   <EmailCalcButton
 *     calcType="snowball"
 *     title="My Rental Snowball Plan"
 *     request={builtRequest}
 *     result={view}
 *   />
 *
 * For generic calculators, pass `inputs` + `outputs` instead.
 */
import { useState } from "react";
import { Mail, Loader2, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

export default function EmailCalcButton({
  calcType,
  title,
  request,
  result,
  inputs,
  outputs,
  summaryLines,
  defaultEmail,
  buttonLabel = "Email me this calculation",
  buttonVariant = "outline",
  dataTestid = "email-calc-btn",
}) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState(defaultEmail || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const send = async () => {
    if (recipient && !/^\S+@\S+\.\S+$/.test(recipient)) {
      toast.error("Enter a valid email or leave blank to use your account email");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        calc_type: calcType,
        title,
        recipient_email: recipient || undefined,
        request,
        result,
        inputs: inputs || {},
        outputs: outputs || {},
        summary_lines: summaryLines,
      };
      const { data } = await api.post("/calc/email-result", payload);
      setSent(true);
      toast.success(`Sent to ${data.recipient} · ${data.pdf_size_kb} KB PDF attached`);
      setTimeout(() => { setOpen(false); setSent(false); setRecipient(defaultEmail || ""); }, 1500);
    } catch (e) {
      const detail = formatApiErrorDetail(e?.response?.data?.detail);
      toast.error(detail || "Email failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant={buttonVariant}
        onClick={() => setOpen(true)}
        data-testid={dataTestid}
      >
        <Mail size={14} className="mr-2" /> {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setSent(false); }}>
        <DialogContent data-testid={`${dataTestid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Email this calculation</DialogTitle>
            <DialogDescription>
              We&apos;ll generate a branded PDF report and email it to you. The PDF
              includes your inputs, key results and a per-row breakdown.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Send to</Label>
              <Input
                type="email"
                placeholder="Your account email (leave blank to use it)"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="mt-1 font-mono"
                data-testid={`${dataTestid}-input`}
                disabled={busy || sent}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Leave blank to send to the email on your Estima account.
              </p>
            </div>
            <div className="rounded-md bg-[#FAFAF7] border border-[#E5E1D6] p-3 text-xs space-y-1">
              <div className="font-medium text-foreground">What&apos;s in the PDF:</div>
              <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
                <li>Branded cover with calculator name and timestamp</li>
                <li>Key results summary (6 most important numbers)</li>
                <li>Full input echo so you can re-create later</li>
                <li>Per-property / per-row detailed breakdown</li>
                <li>Standard Estima disclaimer (not financial advice)</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={send}
              disabled={busy || sent}
              data-testid={`${dataTestid}-send`}
            >
              {sent ? <><Check size={14} className="mr-1" /> Sent</> :
               busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Sending…</> :
               <><Mail size={14} className="mr-1" /> Email it to me</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
